import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { AppSidebar } from './app-sidebar';
import { SiteHeader } from './site-header';
import { SidebarInset, SidebarProvider } from './ui/sidebar';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { TracksList } from './TracksList';
import { TrackEditPanel } from './TrackEditPanel';
import {
  getMusicTracks,
  MUSIC_CATEGORIES,
  type MusicTrack,
} from '@/services/musicTracksService';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Plus } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type SortOption = 'newest' | 'oldest' | 'plays-desc' | 'plays-asc';

export function MusicPage() {
  const { currentUser } = useAuth();
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [filterGenre, setFilterGenre] = useState<string>('all');
  const [filterYear, setFilterYear] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [activePlayerStoragePath, setActivePlayerStoragePath] = useState<string | null>(null);
  const selectedTrack = selectedTrackId ? tracks.find((t) => t.id === selectedTrackId) ?? null : null;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const yearsInData = useMemo(() => {
    const years = new Set(tracks.map((t) => t.year).filter((y): y is number => y != null));
    return Array.from(years).sort((a, b) => b - a);
  }, [tracks]);

  const filteredAndSortedTracks = useMemo(() => {
    let list = [...tracks];
    if (filterGenre !== 'all') {
      list = list.filter((t) => t.category === filterGenre || t.genre === filterGenre);
    }
    if (filterYear !== 'all') {
      const y = parseInt(filterYear, 10);
      list = list.filter((t) => t.year === y);
    }
    if (sortBy === 'newest') {
      list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    } else if (sortBy === 'oldest') {
      list.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
    } else if (sortBy === 'plays-desc') {
      list.sort((a, b) => (b.playCount ?? 0) - (a.playCount ?? 0));
    } else if (sortBy === 'plays-asc') {
      list.sort((a, b) => (a.playCount ?? 0) - (b.playCount ?? 0));
    }
    return list;
  }, [tracks, filterGenre, filterYear, sortBy]);

  const loadTracks = useCallback(async () => {
    if (!currentUser) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const list = await getMusicTracks();
      setTracks(list);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load tracks';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  if (isLoading) {
    return (
      <SidebarProvider
        className="h-svh min-h-0 overflow-hidden"
        style={
          {
            '--sidebar-width': '16rem',
            '--sidebar-width-icon': '3rem',
            '--header-height': 'calc(var(--spacing) * 12)',
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset className="min-h-0 overflow-hidden">
          <SiteHeader tracksCount={0} />
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading tracks...</p>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  if (error) {
    return (
      <SidebarProvider
        className="h-svh min-h-0 overflow-hidden"
        style={
          {
            '--sidebar-width': '16rem',
            '--sidebar-width-icon': '3rem',
            '--header-height': 'calc(var(--spacing) * 12)',
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset className="min-h-0 overflow-hidden">
          <SiteHeader tracksCount={tracks.length} />
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <div className="text-center">
              <p className="text-sm text-destructive mb-2">Error loading tracks</p>
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider
      className="h-svh min-h-0 overflow-hidden"
      style={
        {
          '--sidebar-width': '16rem',
          '--sidebar-width-icon': '3rem',
          '--header-height': 'calc(var(--spacing) * 12)',
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="min-h-0 overflow-hidden">
        <SiteHeader tracksCount={tracks.length} />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col min-w-0 overflow-hidden">
            <div className="flex items-center justify-between border-b px-4 py-3 lg:px-6 shrink-0">
              <h2 className="text-lg font-semibold">Tracks</h2>
              <Button onClick={() => navigate('/app/music/upload')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Track
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3 lg:px-6 shrink-0 bg-muted/30">
              <span className="text-sm font-medium text-muted-foreground">Filters</span>
              <Select value={filterGenre} onValueChange={setFilterGenre}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Genre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All genres</SelectItem>
                  {MUSIC_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {yearsInData.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="plays-desc">Most plays</SelectItem>
                  <SelectItem value="plays-asc">Least plays</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground ml-auto">
                {filteredAndSortedTracks.length} of {tracks.length} tracks
              </span>
            </div>
            <TracksList
              tracks={filteredAndSortedTracks}
              onRefresh={loadTracks}
              canEdit
              selectedTrackId={selectedTrackId}
              onTrackSelect={(track) => setSelectedTrackId(track?.id ?? null)}
              activePlayerStoragePath={activePlayerStoragePath}
              onPlayStart={(path) => setActivePlayerStoragePath(path)}
            />
          </div>
          <div
            className={cn(
              'flex min-h-0 shrink-0 flex-col border-l bg-background transition-all duration-300 ease-in-out overflow-hidden',
              selectedTrack ? 'w-[26rem] min-w-[26rem]' : 'w-0 min-w-0'
            )}
          >
            {selectedTrack && (
              <TrackEditPanel
                track={selectedTrack}
                onClose={() => setSelectedTrackId(null)}
                onSaved={(updated) => {
                  setTracks((prev) =>
                    prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
                  );
                }}
                onDeleted={(trackId) => {
                  setSelectedTrackId(null);
                  setTracks((prev) => prev.filter((t) => t.id !== trackId));
                }}
                activePlayerStoragePath={activePlayerStoragePath}
                onPlayStart={(path) => setActivePlayerStoragePath(path)}
              />
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
